import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ChatWindow } from "@/components/chat-window";
import * as chatService from "@/services/chat";
import type { ChatMessage } from "@/types";
import type { ChatOutletContext } from "./chat-layout";
import { useReportContext } from "./context";

const DRAFT_SESSION_KEY = "new";

interface ChatSessionState {
  messages: ChatMessage[];
  loading: boolean;
  pending: boolean;
}

function initialChatState(testId: string): ChatSessionState {
  return {
    messages: chatService.withWelcomeMessage(testId, []),
    loading: false,
    pending: false,
  };
}

function loadedMessagesForState(
  testId: string,
  loadedMessages: ChatMessage[],
  currentState?: ChatSessionState,
): ChatMessage[] {
  const pendingMessages =
    currentState?.pending === true
      ? currentState.messages.filter((message) =>
          message.id.startsWith("pending-"),
        )
      : [];

  if (pendingMessages.length > 0) {
    return [
      ...loadedMessages.filter((message) => message.id !== "welcome"),
      ...pendingMessages,
    ];
  }

  return chatService.withWelcomeMessage(testId, loadedMessages);
}

/**
 * The only piece that re-renders when switching between sessions.
 * Everything above it (data load, report iframe, session picker) stays
 * mounted and untouched.
 */
export default function PatientReportChatSession() {
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const isDraft = urlSessionId === "new";
  const { token, link, setSessions } = useReportContext();
  const { reportIframeRef } = useOutletContext<ChatOutletContext>();
  const navigate = useNavigate();

  const [chatBySession, setChatBySession] = useState<
    Record<string, ChatSessionState>
  >({});

  // Track the latest session id so async responses for stale sessions can
  // avoid side effects like report highlighting if the user switched away.
  const sessionIdRef = useRef<string | null>(urlSessionId ?? null);
  sessionIdRef.current = urlSessionId ?? null;

  const loadRequestRef = useRef<Record<string, number>>({});

  // When we promote a draft into a real session via the first send, the URL
  // flips from `s/new` → `s/<realId>`. Skip the messages refetch on that one
  // transition because we already have the messages in local state.
  const skipNextLoadRef = useRef(false);

  useEffect(() => {
    if (!urlSessionId) return;

    if (isDraft) {
      setChatBySession((prev) => ({
        ...prev,
        [DRAFT_SESSION_KEY]:
          prev[DRAFT_SESSION_KEY] ?? initialChatState(link.test_id),
      }));
      return;
    }

    if (skipNextLoadRef.current) {
      skipNextLoadRef.current = false;
      return;
    }

    const sessionKey = urlSessionId;
    const requestId = (loadRequestRef.current[sessionKey] ?? 0) + 1;
    loadRequestRef.current[sessionKey] = requestId;

    setChatBySession((prev) => ({
      ...prev,
      [sessionKey]: {
        messages:
          prev[sessionKey]?.messages ??
          chatService.withWelcomeMessage(link.test_id, []),
        loading: true,
        pending: prev[sessionKey]?.pending ?? false,
      },
    }));

    chatService
      .getSessionMessages(sessionKey, token)
      .then((msgs) => {
        if (loadRequestRef.current[sessionKey] !== requestId) return;
        setChatBySession((prev) => ({
          ...prev,
          [sessionKey]: {
            messages: loadedMessagesForState(
              link.test_id,
              msgs,
              prev[sessionKey],
            ),
            loading: false,
            pending: prev[sessionKey]?.pending ?? false,
          },
        }));
      })
      .catch(() => {
        // session may have been deleted or is invalid
        if (loadRequestRef.current[sessionKey] !== requestId) return;
        setChatBySession((prev) => ({
          ...prev,
          [sessionKey]: {
            messages:
              prev[sessionKey]?.messages ??
              chatService.withWelcomeMessage(link.test_id, []),
            loading: false,
            pending: prev[sessionKey]?.pending ?? false,
          },
        }));
      })
      .finally(() => {
        if (loadRequestRef.current[sessionKey] !== requestId) return;
        setChatBySession((prev) => ({
          ...prev,
          [sessionKey]: {
            messages:
              prev[sessionKey]?.messages ??
              chatService.withWelcomeMessage(link.test_id, []),
            loading: false,
            pending: prev[sessionKey]?.pending ?? false,
          },
        }));
      });
  }, [urlSessionId, isDraft, link.test_id, token]);

  const highlightReportFields = useCallback(
    (labels: string[]) => {
      if (!labels.length) return;
      const iframe = reportIframeRef.current;
      if (!iframe?.contentWindow) return;
      iframe.contentWindow.postMessage(
        { type: "cc:report:highlight", labels },
        "*",
      );
    },
    [reportIframeRef],
  );

  async function handleSend(text: string) {
    let sessionId = isDraft ? null : urlSessionId ?? null;
    let sourceKey = isDraft ? DRAFT_SESSION_KEY : (sessionId ?? DRAFT_SESSION_KEY);

    if (!sessionId) {
      const created = await chatService.createSession(token);
      sessionId = created.id;
      sourceKey = sessionId;
      skipNextLoadRef.current = true;
      setChatBySession((prev) => {
        const draftState =
          prev[DRAFT_SESSION_KEY] ?? initialChatState(link.test_id);
        const { [DRAFT_SESSION_KEY]: _draft, ...rest } = prev;
        return {
          ...rest,
          [created.id]: {
            ...draftState,
            loading: false,
            pending: false,
          },
        };
      });
      const updated = await chatService.listSessions(token);
      setSessions(updated);
      navigate(`/r/${token}/s/${created.id}`, { replace: true });
    }

    const sendSession = sessionId;

    const optimistic: ChatMessage = {
      id: `pending-${Date.now()}`,
      testId: link.test_id,
      sender: "user",
      text,
      timestamp: new Date().toISOString(),
    };
    setChatBySession((prev) => {
      const current = prev[sourceKey] ?? initialChatState(link.test_id);
      return {
        ...prev,
        [sendSession]: {
          messages: [
            ...current.messages.filter((m) => m.id !== "welcome"),
            optimistic,
          ],
          loading: false,
          pending: true,
        },
      };
    });

    try {
      const [userMsg, assistantMsg] = await chatService.sendMessage(
        sendSession,
        text,
        token,
      );
      setChatBySession((prev) => {
        const current = prev[sendSession] ?? initialChatState(link.test_id);
        return {
          ...prev,
          [sendSession]: {
            messages: [
              ...current.messages.filter((m) => m.id !== optimistic.id),
              userMsg,
              assistantMsg,
            ],
            loading: false,
            pending: false,
          },
        };
      });
      if (
        sessionIdRef.current === sendSession &&
        assistantMsg.highlights?.length
      ) {
        setTimeout(
          () => highlightReportFields(assistantMsg.highlights!),
          200,
        );
      }
    } catch {
      setChatBySession((prev) => {
        const current = prev[sendSession];
        if (!current) return prev;
        return {
          ...prev,
          [sendSession]: {
            messages: current.messages.filter((m) => m.id !== optimistic.id),
            loading: false,
            pending: false,
          },
        };
      });
    } finally {
      setChatBySession((prev) => {
        const current = prev[sendSession];
        if (!current || !current.pending) return prev;
        return {
          ...prev,
          [sendSession]: {
            ...current,
            pending: false,
          },
        };
      });
    }
  }

  const activeSessionKey = isDraft
    ? DRAFT_SESSION_KEY
    : urlSessionId ?? DRAFT_SESSION_KEY;
  const activeState =
    chatBySession[activeSessionKey] ?? initialChatState(link.test_id);

  return (
    <ChatWindow
      messages={activeState.messages}
      onSend={handleSend}
      isLoading={activeState.loading}
      isAwaiting={activeState.pending}
    />
  );
}
